import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';

import { getEntries, getKeys, getValues } from '../utils/record';

import useEvent from './useEvent';

type MapEntries<TKey extends MapKey, TValue> = [TKey, TValue][] | (readonly [TKey, TValue])[];

type MapInput<TKey extends MapKey, TValue> =
  | MapEntries<TKey, TValue>
  | Map<TKey, TValue>
  | Partial<{ [Key in TKey]: TValue }>;

type MapKey = string | number;

type Options<T = unknown> = {
  areEqual?: (prev: T, next: T) => boolean; // should be stable if reference equality for the return value matters
  enableReinitialize?: boolean;
};

export type MapReturn<TKey extends MapKey, TValue> = ReturnType<typeof useMap<TKey, TValue>>;

const DEFAULT_OPTIONS = {
  areEqual: isEqual,
  enableReinitialize: true,
} satisfies Options;

const toEntries = <TKey extends MapKey, TValue>(
  input: MapInput<TKey, TValue> | null | undefined,
) => {
  if (!input || Array.isArray(input)) return input;
  if (input instanceof Map) return input.entries().toArray();

  return Object.entries(input).filter(([_, value]) => value !== undefined) as MapEntries<
    TKey,
    TValue
  >;
};

const toMap = <TKey extends MapKey, TValue>(input: MapInput<TKey, TValue> | null | undefined) => {
  const entries = toEntries(input);

  return new Map(entries);
};

const useMap = <TKey extends MapKey, TValue>(
  input?: MapInput<TKey, TValue> | null | undefined,
  options?: Options<TValue>,
) => {
  const [map, setMap] = useState(() => toMap(input));

  const areEqual = options?.areEqual ?? DEFAULT_OPTIONS.areEqual;
  const enableReinitialize = options?.enableReinitialize ?? DEFAULT_OPTIONS.enableReinitialize;

  const entries = useCallback(() => getEntries(map), [map]);

  const forEach = useCallback(
    (callback: (entry: [TKey, TValue]) => void) =>
      map.forEach((value, key) => callback([key, value])),
    [map],
  );

  const get = useCallback((key: TKey) => map.get(key), [map]);

  const getOrAppend = useCallback(
    (key: TKey, defaultValue: TValue) => {
      const value = map.get(key);

      if (value !== undefined) return value;

      setMap((prevMap) => new Map(prevMap).set(key, defaultValue));

      return defaultValue;
    },
    [map],
  );

  const getOrAppendLazy = useCallback(
    (key: TKey, defaultValue: () => TValue) => {
      const value = map.get(key);

      if (value !== undefined) return value;

      const setValue = defaultValue();

      setMap((prevMap) => new Map(prevMap).set(key, setValue));

      return setValue;
    },
    [map],
  );

  const has = useCallback((key: TKey) => map.has(key), [map]);

  const keys = useCallback(() => getKeys(map), [map]);

  const merge = useCallback(
    (other: MapInput<TKey, TValue>) =>
      setMap((prevMap) => {
        const otherEntries = toEntries(other);

        if (!otherEntries?.length) return prevMap;

        const nextMap = new Map(prevMap);

        let hasChanges = false;

        otherEntries.forEach(([key, nextValue]) => {
          const prevValue = nextMap.get(key);

          if (prevValue !== undefined && areEqual(prevValue, nextValue)) return;

          nextMap.set(key, nextValue);

          hasChanges = true;
        });

        return hasChanges ? nextMap : prevMap;
      }),
    [areEqual],
  );

  const remove = useCallback(
    (key: TKey) =>
      setMap((prevMap) => {
        if (!prevMap.has(key)) return prevMap;

        const nextMap = new Map(prevMap);

        nextMap.delete(key);

        return nextMap;
      }),
    [],
  );

  const removeAll = useCallback(
    () => setMap((prevMap) => (prevMap.size === 0 ? prevMap : new Map())),
    [],
  );

  const set = useCallback(
    (key: TKey, value: TValue) =>
      setMap((prevMap) => {
        const prevValue = prevMap.get(key);

        if (prevValue !== undefined && areEqual(prevValue, value)) return prevMap;

        return new Map(prevMap).set(key, value);
      }),
    [areEqual],
  );

  const setWithUpdater = useCallback(
    (key: TKey, updater: (prevValue: TValue | undefined) => TValue) =>
      setMap((prevMap) => {
        const prevValue = prevMap.get(key);
        const nextValue = updater(prevValue);

        if (prevValue !== undefined && areEqual(prevValue, nextValue)) return prevMap;

        return new Map(prevMap).set(key, nextValue);
      }),
    [areEqual],
  );

  const values = useCallback(() => getValues(map), [map]);

  const handleEqualityCheck = useEvent(areEqual);

  useEffect(() => {
    if (!enableReinitialize) return;

    setMap((prevMap) => {
      const nextMap = toMap(input);

      const nextEntries = getEntries(nextMap);
      const prevEntries = getEntries(prevMap);

      if (nextEntries.length !== prevEntries.length) return nextMap;

      return nextEntries.every(([key, nextValue]) => {
        const prevValue = prevMap.get(key);

        if (prevValue === undefined) return nextValue === undefined;

        return handleEqualityCheck(prevValue, nextValue);
      })
        ? prevMap
        : nextMap;
    });
  }, [enableReinitialize, handleEqualityCheck, input]);

  return useMemo(
    () => ({
      entries,
      forEach,
      get,
      getOrAppend,
      getOrAppendLazy,
      has,
      isEmpty: map.size === 0,
      keys,
      map,
      merge,
      remove,
      removeAll,
      set,
      setWithUpdater,
      size: map.size,
      values,
    }),
    [
      entries,
      forEach,
      get,
      getOrAppend,
      getOrAppendLazy,
      has,
      keys,
      map,
      merge,
      remove,
      removeAll,
      set,
      setWithUpdater,
      values,
    ],
  );
};

export default useMap;
